import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { authApi } from '@/api';
import { ORG_SIGN_IN_PATH } from '@/constants/authRoutes';
import { useState } from 'react';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { AuthCard } from '@/core/auth/components/AuthCard';
import { AuthInput } from '@/core/auth/components/AuthInput';
import { AuthButton } from '@/core/auth/components/AuthButton';

type FormData = { email: string };

export function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const schema = z.object({
    email: z.string().email(t('validation.email.invalid', { ns: 'validation', defaultValue: 'Enter a valid email' })),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      await authApi.forgotPassword(data);
      setSent(true);
      notify.t.success('forgotPassword.sentToast', { ns: 'auth' });
    } catch (err) {
      showApiError(err, t('forgotPassword.submit'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard title={t('forgotPassword.title')}>
      {sent ? (
        <p className="text-sm text-slate-600 dark:text-slate-400">{t('forgotPassword.sentBody')}</p>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <AuthInput
            label={t('forgotPassword.email')}
            type="email"
            autoComplete="email"
            error={errors.email?.message}
            {...register('email')}
          />
          <AuthButton type="submit" loading={loading}>
            {t('forgotPassword.submit')}
          </AuthButton>
        </form>
      )}
      <p className="mt-4 text-center text-sm">
        <Link to={ORG_SIGN_IN_PATH} className="text-primary-600 hover:underline dark:text-primary-400">
          {t('forgotPassword.backToSignIn')}
        </Link>
      </p>
    </AuthCard>
  );
}
